import React, { useContext, useRef } from 'react';
import ArticleLink, { LinkStyleType } from '../GuideNavigation/ArticleLink/ArticleLink';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import { EndUserComponent, EndUserQueryParams } from '@aurora/shared-types/pages/enums';
import useTranslation from './../../useTranslation';
import type {
  MessagesForManualSortQuery,
  MessagesForManualSortQueryVariables
} from '../../../types/graphql-types';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import manuallyOrderedArticlesQuery from './../GuideStructure/ManualSortArticleList/MessagesForManualSort.query.graphql';
import Pager from '@aurora/shared-client/components/common/Pager/Pager';
import type { PagerInfiniteScrollTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import PaginationHelper from '@aurora/shared-client/helpers/ui/PaginationHelper/PaginationHelper';
import type { ArticleConstraints } from '@aurora/shared-generated/types/graphql-schema-types';
import SideNavigationWithMobileOverlay from '../SideNavigationWithMobileOverlay/SideNavigationWithMobileOverlay';

interface Props {
  /**
   * Class name(s) to apply to the wrapping element.
   */
  className?: string;
}

/**
 * Manually ordered article navigation
 *
 * @author Amit Agrawal
 */
const ManuallyOrderedArticleNavigation: React.FC<Props> = ({ className }) => {
  const {
    contextMessage: { id: contextMessageId, uid, board },
    contextNode
  } = useContext(AppContext);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.MANUALLY_ORDERED_ARTICLE_NAVIGATION
  );

  const containerReference = useRef<HTMLDivElement>(null);
  const MANUAL_SORT_ARTICLES_PAGE_SIZE = 999;

  const rootMargin: number = containerReference?.current?.offsetHeight || 0;

  const pagerVariant: PagerInfiniteScrollTypeAndProps = {
    type: PagerVariant.INFINITE_SCROLL,
    props: {
      rootReference: containerReference,
      rootMargin: `0px 0px ${rootMargin}px 0px`
    }
  };

  const articleConstraints: ArticleConstraints = {
    boardId: { eq: contextNode.id }
  };

  const manualSortQueryVariables: MessagesForManualSortQueryVariables = {
    constraints: articleConstraints,
    first: MANUAL_SORT_ARTICLES_PAGE_SIZE,
    useManualSortArticleMetadata: false
  };

  const queryResult = useQueryWithTracing<
    MessagesForManualSortQuery,
    MessagesForManualSortQueryVariables
  >(module, manuallyOrderedArticlesQuery, {
    variables: manualSortQueryVariables
  });

  if (textLoading || queryResult.loading) {
    return null;
  }

  const paginationHelper = PaginationHelper.fromQueryResult<
    MessagesForManualSortQuery,
    MessagesForManualSortQueryVariables
  >(queryResult, MANUAL_SORT_ARTICLES_PAGE_SIZE, 'manuallyOrderedArticles');

  return (
    <SideNavigationWithMobileOverlay
      buttonTitle={formatMessage('buttonText')}
      className={className}
    >
      {queryResult.data?.manuallyOrderedArticles?.edges.map(article => (
        <ArticleLink
          key={article.node.id}
          linkStyleType={LinkStyleType.CONTENT}
          article={{ ...article.node, board, uid }}
          contextMessageId={contextMessageId}
          useManualSortNav
          additionalQueryParams={{
            [EndUserQueryParams.NODE_ID]: article.node.board.id,
            [EndUserQueryParams.ARTICLE_ID]: article.node.id
          }}
        />
      ))}
      <Pager
        loadPage={paginationHelper.loadPage}
        pageInfo={paginationHelper.pageInfo}
        variant={pagerVariant}
      />
    </SideNavigationWithMobileOverlay>
  );
};

export default ManuallyOrderedArticleNavigation;
